import React, { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import FeatherIcon from 'feather-icons-react';
import { Space, Tooltip, Switch, Popover, Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { UserFilterButtonContent } from './UserFilterButtonContent';
import ModalPassword from './ModalPassword';
import ModalUserSessions from './ModalUserSessions';
import ActionsMobileUser from './ActionsMobileUser';
import { DataTable } from '../../../components/dataTable/data-table';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';
import { useNotifications } from '../../../hooks/useNotifications';
import { Cards } from '../../../components/cards/frame/cards-frame';
import { Button } from '../../../components/buttons/buttons';
import { profileTypes } from '../../../config/constants/userConstants';
import { useTableUser } from '../hook/useTableUser';
import { BulletIcon } from '../../../components/utilities/appIcons';
import { useColorList } from '../../../hooks/useColorList';
import { GenericButton } from '../../../components/buttons/table-buttons/TableButtons';

const editableUsers = [profileTypes.INTERESTED.id, profileTypes.TECHNICIAN.id, profileTypes.OPERATOR.id];

export const UsersTable = () => {
  const { translate: t } = useDictionaryTranslation();
  const { getColorById } = useColorList();
  const [selectedProfileType, setSelectedProfileType] = useState(null);
  const { showErrorNotification, showConfirmNotification, showSuccessNotification } = useNotifications();
  const { isLoading, reloadData, tableData, getData, removeUser, changeUserStatus } = useTableUser({
    selectedProfileType,
  });
  const { isAdminOrMaster } = useSelector((state) => ({
    isAdminOrMaster: [profileTypes.ADMIN.id, profileTypes.MASTER.id].includes(
      state.auth?.currentUser.profile.idProfile,
    ),
  }));
  const [modalPasswordData, setModalPasswordData] = useState({
    show: false,
    userId: null,
  });
  const [modalSessionsData, setModalSessionsData] = useState({
    show: false,
    userId: null,
  });


  const bulletedText = (text, color, tooltipText) => {
    return (
      <>
        <Tooltip title={tooltipText}>
          <>{color ? <BulletIcon color={color} /> : null}</>
        </Tooltip>
        {text && <span>{text}</span>}
      </>
    );
  };

  const getRemoveAction = (user) => () => {
    showConfirmNotification({
      title: t('areYouSure'),
      content: t('recordWillBeRemoved', { recordDescription: user.profile.identification }),
      onOk: async () => {
        try {
          await removeUser(user._id);
          showSuccessNotification({
            title: 'success',
            description: 'successRemovingRecord',
          });
          reloadData();
        } catch (error) {
          showErrorNotification({
            title: 'error',
            description: 'errorRemovingRecord',
          });
        }
      },
    });
  };

  const changeStatusAction = async (user) => {
    try {
      await changeUserStatus(user._id, !user.profile?.status);
      showSuccessNotification({
        title: 'success',
        description: 'successUpdatingRecord',
      });
      reloadData();
    } catch (error) {
      showErrorNotification({
        title: 'error',
        description: 'errorUpdatingRecord',
      });
    }
  };

  const askToChangeUserStatus = (user) => () => {
    if (user?.profile?.status) {
      showConfirmNotification({
        title: t('areYouSure'),
        content: t('userWillBeDisabled'),
        onOk: () => changeStatusAction(user),
      });
    } else {
      changeStatusAction(user);
    }
  };

  const getEditUserPasswordHandler = (user) => () => {
    setModalPasswordData({
      show: true,
      userId: user._id,
    });
  };

  const onCloseModalPassword = () => {
    setModalPasswordData({
      show: false,
      userId: null,
    });
  };

  const getOpenUserSessionsHandler = (user) => () => {
    setModalSessionsData({
      show: true,
      userId: user._id,
    });
  };

  const onCloseModalSessions = () => {
    setModalSessionsData({
      show: false,
      userId: null,
    });
  };

  const columns = [
    {
      key: 'userType',
      title: t('userType'),
      dataIndex: 'profile.idProfile',
      width: '15%',
      render: (_, record) => {
        const profileKey = Object.values(profileTypes).find((value) => record?.profile?.idProfile === value.id);
        const profileName = t(profileKey?.name ?? '');
        return profileName;
      },
    },
    {
      key: 'username',
      dataIndex: 'username',
      title: t('username'),
      width: '15%',
    },
    {
      key: 'identification',
      title: t('identification'),
      width: '15%',
      render: (_, record) => {
        return record.profile?.identification;
      },
    },
    {
      key: 'name',
      title: t('name'),
      render: (_, record) => {
        return record.profile?.name;
      },
    },
    {
      key: 'specialitiesInfo',
      dataIndex: 'specialitiesInfo',
      title: t('specialities'),
      width: '5%',
      render: (_, record) => {
        return (
          <Row gutter={[2, 2]} wrap>
            {record.profile.specialitiesInfo?.map((item) => (
              <Col key={item?._id}>
                {bulletedText(null, getColorById(item.infoColor?.id), `${item?.name}`)}
              </Col>
            ))}
          </Row>
        );
      },
    },
    {
      key: 'status',
      title: t('status'),
      render: (_, record) => {
        return <Switch checked={record.profile?.status} onClick={askToChangeUserStatus(record)} />;
      },
    },
    {
      key: 'actions',
      title: t('actions'),
      align: 'center',

      render: (_, record) => (
        <Space size="small">
          <Popover
            content={
              <>
                {record?.profile?.idProfile === profileTypes.TECHNICIAN.id ? (
                  <ActionsMobileUser userId={record._id} />
                ) : null}
              </>
            }
            placement="bottom"
            overlayInnerStyle={{ padding: '0px' }}
          >
            <GenericButton
              primaryColor="#d9d9d9"
              textColor={record?.profile?.idProfile === profileTypes.TECHNICIAN.id ? '#5c617f' : '#b8b8b8'}
              icon="more-horizontal"
              disabled={record?.profile?.idProfile !== profileTypes.TECHNICIAN.id}
            />
          </Popover>

          <Tooltip title={t('updatePasswordText')}>
            <GenericButton
              type="gray"
              icon="lock"
              onClick={getEditUserPasswordHandler(record)}
              disabled={!record.profile?.status}
            />
          </Tooltip>
          {isAdminOrMaster ? (
            <Tooltip title={t('sessions')}>
              <GenericButton type="gray" icon="monitor" onClick={getOpenUserSessionsHandler(record)} />
            </Tooltip>
          ) : null}
          <Tooltip title={t('edit')}>
            <Link to={`/user/${record._id}`}>
              <GenericButton
                type="secondary"
                icon="edit-3"
                disabled={!record.profile?.status}
              />
            </Link>
          </Tooltip>
          <Tooltip title={t('remove')}>
            <GenericButton
              type="danger"
              onClick={getRemoveAction(record)}
              icon="trash"
              disabled={!editableUsers.includes(record.profile?.idProfile) || !record.profile?.status}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Cards headless>
      <DataTable
        rowKey="_id"
        onChange={getData}
        columns={columns}
        isLoading={isLoading}
        tableData={tableData}
        headerData={{
          show: true,
          itemsName: t('users'),
          actions: (
            <>
              <Link to="/user">
                <Button type="primary" icon={<PlusOutlined />} />
              </Link>
              <Popover content={<UserFilterButtonContent onSelect={setSelectedProfileType} />} placement="bottom">
                <Button style={{ background: '#00af50' }}>
                  <FeatherIcon icon="filter" style={{ color: 'white' }} />
                </Button>
              </Popover>
            </>
          ),
        }}
      />
      {modalPasswordData?.show ? (
        <ModalPassword
          userId={modalPasswordData.userId}
          show={modalPasswordData.show}
          closeModal={onCloseModalPassword}
        />
      ) : null}
      {modalSessionsData?.show ? (
        <ModalUserSessions
          userId={modalSessionsData.userId}
          show={modalSessionsData.show}
          closeModal={onCloseModalSessions}
        />
      ) : null}
    </Cards>
  );
};
